import React, { useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle, Grid, Box, Typography, styled } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Modal from '../model';
import { DraggablePaper } from '../model';
import { sendMessageApiCall } from '../chat/Action';
import { translate } from '../../helper';
import { Label } from '../form/TextInput';
import MessageComposer from '../message-composer';
import { messageSchema } from '../../schema/vendorSchema';
import useUploadControllers from '../hooks/useUploadControllers';
import Utils from '../../utils';

const ModalWrapper = styled('div')(({ theme }) => ({
  '& .MuiDialogContent-root': {
    'backgroundColor': '#F3F4F6',
    'padding': 'var(--sp-md2)',
    'borderRadius': 'var(--sp-md2)',
    'marginBottom': 'var(--sp-md2)'
  },
  '& .detals': {
    'width': '30%',
    fontSize: 'var(--fs-md2)',
  }
}));

const OrderDetailsBox = styled(Box)({
  'backgroundColor': '#F3F4F6',
  'width': '100%',
  'padding': 'var(--sp-md2)',
  'borderRadius': 'var(--sp-md2)',
  'marginBottom': 'var(--sp-md2)'
});

/**
 * Unified ChatMessageModal component
 * @param {Object} props
 * @param {boolean} props.isOpen - Modal open state
 * @param {Function} props.onClose - Close callback
 * @param {Object} props.orderDetail - Order details object
 * @param {Object} props.user - Current user object
 * @param {string} props.vendorId - Vendor ID
 * @param {Array} props.messageList - List of messages
 * @param {string} props.type - Type of modal: 'reply' | 'startThread' | 'respond'
 * @param {string} props.modalComponent - Component type: 'dialog' | 'modal'
 * @param {boolean} props.draggable - Make modal draggable
 * @param {string} props.maxWidth - Max width of modal (e.g., 'calc(var(--fs-hero)*15)')
 * @param {boolean} props.showOrderDetails - Show order details section
 * @param {string} props.width - Width style (e.g., '75vw')
 */
const ChatMessageModal = ({
  isOpen,
  onClose,
  orderDetail,
  user,
  vendorId,
  messageList = [],
  type = 'reply', // 'reply' | 'startThread' | 'respond'
  modalComponent = 'dialog', // 'dialog' | 'modal'
  draggable = false,
  maxWidth = 'calc(var(--fs-hero)*15)',
  showOrderDetails = false,
  width = null
}) => {
  const [files, setFiles] = React.useState([]);
  const { uploadControllers, handleClose } = useUploadControllers(setFiles, onClose, isOpen);

  // ✅ Track message count when modal OPENS
  const initialMessageCountRef = React.useRef(null);

  // ✅ When modal opens, set initial message count
  useEffect(() => {
    if (isOpen) {
      initialMessageCountRef.current = messageList?.length || 0;
    }
  }, [isOpen]);

  // ✅ Watch messageList AFTER modal opens
  useEffect(() => {
    if (!isOpen || initialMessageCountRef.current === null) return;

    const currentCount = messageList?.length || 0;
    const initialCount = initialMessageCountRef.current;

    // Only close if NEW message added AFTER modal opened AND it's sent by current user
    if (currentCount > initialCount) {
      const latestMessage = messageList[messageList.length - 1];
      const isMessageFromCurrentUser = String(user?.id) === String(latestMessage?.message_sent_by_user_id);

      if (isMessageFromCurrentUser) {
        setTimeout(() => {
          Utils.showAlert(1, 'Successfully sent!');
          handleClose();
        }, 300);
      }
    }
  }, [messageList, isOpen, handleClose, user]);

  const handleSubmit = (values, attachments, actions) => {
    sendMessageApiCall(
      {
        body: values.text,
        vendor_id: vendorId,
        order_id: orderDetail?.id,
        data: { attachments }
      },
      actions.setSubmitting,
      () => {
        // Modal will close via messageList watch
      }
    );
  };

  // Get title based on type
  const getTitle = () => {
    switch (type) {
      case 'startThread':
        return translate('startThread');
      case 'respond':
        return 'Respond';
      case 'reply':
      default:
        return translate('reply');
    }
  };

  // Content for order details
  const renderOrderDetails = () => {
    if (!showOrderDetails || !orderDetail) return null;

    return (
      <OrderDetailsBox>
        <Box marginBottom={'var(--sp-md2)'} display={'flex'}>
          <Typography className="detals">{translate('customerPo')}:</Typography>
          <Typography sx={{ fontSize: 'var(--fs-md2)' }}>{orderDetail?.id}</Typography>
        </Box>
        <Box marginBottom={'var(--sp-md2)'} display={'flex'}>
          <Typography className="detals">{translate('quantity')}:</Typography>
          <Typography sx={{ fontSize: 'var(--fs-md2)' }}>
            {orderDetail?.parts?.reduce((sum, part) => sum + (part.quantity || 0), 0) || 0}
          </Typography>
        </Box>
        <Box marginBottom={'var(--sp-md2)'} display={'flex'}>
          <Typography className="detals">{translate('address')}:</Typography>
          <Typography sx={{ fontSize: 'var(--fs-md2)' }}>{orderDetail?.shipping_address || 'N/A'}</Typography>
        </Box>
      </OrderDetailsBox>
    );
  };

  // Render message composer section
  const renderMessageComposer = (showLabel = true) => (
    <Grid item xs={12}>
      {showLabel && <Label>{translate('reply')}</Label>}
      <MessageComposer
        placeholder={messageList.length > 0 ? translate('enterReply') : translate('writeMessage')}
        onSubmit={handleSubmit}
        inputMinHeight={type === 'respond' ? 180 : 'calc(var(--fs-hero)*6.25)'}
        inputMaxHeight={type === 'respond' ? 280 : 'calc(var(--fs-hero)*8)'}
        validationSchema={messageSchema}
        btnText={messageList.length > 0 ? 'reply' : 'sendMessage'}
        uploadIdValue={orderDetail?.id}
        files={files}
        setFiles={setFiles}
        uploadControllers={uploadControllers}
        isMobile={type === 'respond'}
      />
    </Grid>
  );

  // Dialog version (ReplyChatModal, ResponseModal)
  if (modalComponent === 'dialog') {
    const dialogProps = {
      open: isOpen,
      onClose: handleClose
    };

    if (draggable) {
      dialogProps.maxWidth = false;
      dialogProps.PaperProps = { sx: { maxWidth: 'unset' } };
      dialogProps.PaperComponent = DraggablePaper;
      dialogProps.sx = {
        '& .MuiDialog-container': {
          '& .MuiDialog-paper': {
            width: '100%',
            maxWidth: `${maxWidth} !important`
          }
        }
      };
    } else if (width) {
      // For ResponseModal style (mobile)
      dialogProps.sx = {
        '& .MuiDialogContent-root': {
          'width': width
        },
        '& .MuiDialogTitle-root': {
          'fontSize': '16px',
          fontWeight: 550
        }
      };
    }

    return (
      <Dialog {...dialogProps}>
        {type === 'respond' ? (
          <>
            <DialogTitle style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div>{getTitle()}</div>
              <CloseIcon onClick={handleClose} style={{ fontSize: '15px' }} />
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={0}>
                {renderOrderDetails()}
                {renderMessageComposer(false)}
              </Grid>
            </DialogContent>
          </>
        ) : (
          <ModalWrapper>
            <DialogContent>
              <Grid container spacing="calc(var(--sp-xxs)*2)">
                {/* Header row */}
                <Grid item xs={12}>
                  <Grid container spacing="calc(var(--sp-xxs)*2)">
                    <Grid
                      item
                      xs={8}
                      data-draggable-handle="true"
                      style={{ cursor: 'move', display: 'flex', alignItems: 'center' }}
                    >
                      <Label>{getTitle()}</Label>
                    </Grid>
                    <Grid style={{ textAlign: 'right' }} item xs={4}>
                      <CloseIcon onClick={handleClose} style={{ fontSize: 'var(--fs-xl)', cursor: 'pointer' }} />
                    </Grid>
                  </Grid>
                </Grid>
                {/* Order details and message composer */}
                {renderOrderDetails()}
                {renderMessageComposer(false)}
              </Grid>
            </DialogContent>
          </ModalWrapper>
        )}
      </Dialog>
    );
  }

  // Modal version (StartThreadModal)
  return (
    <Modal
      title={getTitle()}
      isOpen={isOpen}
      onClose={handleClose}
      draggable={draggable}
    >
      <ModalWrapper>
        {renderOrderDetails()}
        <Grid container>
          {renderMessageComposer(false)}
        </Grid>
      </ModalWrapper>
    </Modal>
  );
};

export default ChatMessageModal;
